if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isSlider = this.classList.contains('media-gallery--slider');
        if (this.isSlider) {
          this.elements.progress = this.querySelector('.product__media-progress');
          this.elements.viewer.addEventListener('slideChanged', this.updateProgress.bind(this));
          customElements.whenDefined('slider-component').then(() => {
            this.syncArrowsToCurrentImage();
            this.enableMouseDrag();
          });
        }
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // The slider layout scrolls only the gallery, never the page
          if (this.elements.thumbnails || this.isSlider) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if ((!this.elements.thumbnails && !this.isSlider) || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      // Mouse drag. Touch already swipes natively (scroll-snap), so this handles mouse pointers only. Dragging scrolls
      // the slider with snapping off; on release it settles on the image it started from, or the next/previous one
      // after a drag past 15% of a slide or a flick. Drags stop at the first and last image, like the arrows.
      // A drag ends in a click on whatever is under the pointer, so that click is swallowed to keep the zoom button
      // from opening. Only galleries with more than one image get the draggable class (and grab cursor), set in
      // product-media-gallery.liquid.
      enableMouseDrag() {
        const slider = this.elements.viewer.slider;
        if (!slider || !slider.classList.contains('product__media-list--draggable')) return;

        let drag = null;
        let dragged = false;

        // Stops the browser's own image drag (the ghost image)
        slider.addEventListener('dragstart', (event) => event.preventDefault());

        slider.addEventListener('pointerdown', (event) => {
          dragged = false;
          if (event.pointerType !== 'mouse' || event.button !== 0 || this.slideCount() < 2) return;
          const step = this.slideStep();
          drag = {
            pointerId: event.pointerId,
            x: event.clientX,
            time: event.timeStamp,
            scrollLeft: slider.scrollLeft,
            index: Math.round(slider.scrollLeft / step),
            step,
          };
        });

        slider.addEventListener('pointermove', (event) => {
          if (!drag || event.pointerId !== drag.pointerId) return;
          const dx = event.clientX - drag.x;
          if (!dragged) {
            if (Math.abs(dx) < 5) return;
            dragged = true;
            slider.setPointerCapture(drag.pointerId);
            slider.classList.add('is-dragging');
          }
          slider.scrollLeft = drag.scrollLeft - dx;
        });

        const endDrag = (event) => {
          if (!drag || event.pointerId !== drag.pointerId) return;
          const { x, time, index, step } = drag;
          drag = null;
          if (!dragged) return;

          // Nearest image to where the drag ended; if that's still the starting one, a long-enough drag or a flick
          // moves one image on
          const dx = event.clientX - x;
          const flick = Math.abs(dx) / Math.max(event.timeStamp - time, 1) > 0.5;
          let target = Math.round(slider.scrollLeft / step);
          if (target === index && (Math.abs(dx) > step * 0.15 || flick)) target += dx < 0 ? 1 : -1;
          target = Math.max(0, Math.min(target, this.slideCount() - 1));

          // Snapping stays off until the glide to the target ends, so the browser doesn't re-snap mid-glide
          let settled = false;
          const settle = () => {
            if (settled) return;
            settled = true;
            slider.classList.remove('is-dragging');
          };
          slider.addEventListener('scrollend', settle, { once: true });
          window.setTimeout(settle, 600);
          slider.scrollTo({ left: target * step, behavior: 'smooth' });
        };
        slider.addEventListener('pointerup', endDrag);
        slider.addEventListener('pointercancel', endDrag);

        slider.addEventListener(
          'click',
          (event) => {
            if (!dragged) return;
            dragged = false;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );
      }

      // The arrows follow the current image (the one the pagination bar shows), not the scroll position. SliderComponent
      // sets them from the scroll position on every scroll, so mid-drag or mid-swipe on the last image the next arrow
      // came back (and on the first image, the previous arrow). This listener runs after SliderComponent's and resets
      // them from currentPage, which only changes once the slider is past the halfway point to another image.
      syncArrowsToCurrentImage() {
        const viewer = this.elements.viewer;
        if (!viewer || !viewer.slider || !viewer.prevButton || !viewer.nextButton) return;
        const sync = () => {
          viewer.prevButton.toggleAttribute('disabled', viewer.currentPage <= 1);
          viewer.nextButton.toggleAttribute('disabled', viewer.currentPage >= this.slideCount());
        };
        viewer.slider.addEventListener('scroll', sync);
        sync();
      }

      // Distance from one slide to the next (the slide width, unless the slides ever get a gap)
      slideStep() {
        const items = this.elements.viewer.sliderItemsToShow;
        if (items.length > 1) return items[1].offsetLeft - items[0].offsetLeft;
        return this.elements.viewer.slider.clientWidth;
      }

      // The slider shows one image per page, so pages = visible slides. SliderComponent's own totalPages can come out
      // one high when the column width is fractional (it floors clientWidth / rounded slide offset).
      slideCount() {
        return this.elements.viewer.sliderItemsToShow.length;
      }

      updateProgress(event) {
        if (!this.elements.progress) return;
        this.elements.progress.style.setProperty('--media-count', this.slideCount());
        this.elements.progress.style.setProperty('--media-index', event.detail.currentPage - 1);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
